import React, { useState, useEffect, useContext } from 'react'
import { toast } from 'react-toastify';
import { Search, X, Upload, Download, FileSpreadsheet } from 'lucide-react';
import * as XLSX from 'xlsx';
import moment from 'moment';
import {
  getProductMaster,
  create_product_details,
  get_product_details,
  update_product_details,
  delete_product_details,
  adjustProductDetailsStock,
  bulkUploadProductDetails
} from "../../../Utility/Api";
import { WarehouseContext } from "../../../Utility/UserContext";
import { NaviGate } from "../../../Utility/function";

const Pod_details = () => {
  const { showLoader, hideLoader, UserItem, shopItem, selectedWarehouse, warehouse } = useContext(WarehouseContext);

  // State management
  const [products, setProducts] = useState([]);
  const [productDetails, setProductDetails] = useState([]);
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [selectedDetail, setSelectedDetail] = useState(null);
  const [loading, setLoading] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);
  const [filterProductId, setFilterProductId] = useState("");
  const [searchQuery, setSearchQuery] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");

  // Debounce search query (300ms delay)
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(searchQuery);
    }, 300);

    return () => clearTimeout(timer);
  }, [searchQuery]);

  // Stock Adjustment States
  const [showAdjustModal, setShowAdjustModal] = useState(false);
  const [adjustDetail, setAdjustDetail] = useState(null);
  const [adjustQty, setAdjustQty] = useState("");
  const [adjustType, setAdjustType] = useState("with_val");
  const [adjustAction, setAdjustAction] = useState("add");
  const [adjustPrice, setAdjustPrice] = useState("");
  const [adjustDate, setAdjustDate] = useState(moment().format("YYYY-MM-DD"));

  // Bulk Upload States
  const [showBulkModal, setShowBulkModal] = useState(false);
  const [bulkDate, setBulkDate] = useState(moment().format("YYYY-MM-DD"));
  const [bulkFile, setBulkFile] = useState(null);
  const [bulkFileName, setBulkFileName] = useState("");
  const [bulkParsedData, setBulkParsedData] = useState([]);
  const [isUploadingBulk, setIsUploadingBulk] = useState(false);

  // Form data
  const [formData, setFormData] = useState({
    p_id: '',
    p_code: '',
    p_weight: '',
    // warehouse_id: selectedWarehouse?.id || '',
    branch_id: '',
    shop_id: shopItem?.shop_id || '',
    p_price: '',
    pbox_qty: '',
    pbox_unit: '',
    pbox_code: '',
    pbox_price: '',
    created_by: UserItem?.user_id || ''
  });

  // Form errors
  const [errors, setErrors] = useState({});

  // Fetch data on component mount
  useEffect(() => {
    fetchProducts();
    fetchProductDetails();
  }, []);

  // Fetch products list
  const fetchProducts = async () => {
    showLoader();
    try {
      const response = await getProductMaster();
      if (response.data && response.data.data) {
        setProducts(response.data.data);
      }
    } catch (error) {
      console.error("Error fetching products:", error);
      toast.error("Failed to fetch products");
    } finally {
      hideLoader();
    }
  };

  // Fetch product details
  const fetchProductDetails = async (page = 1, text = "") => {
    showLoader();
    try {
      const params = {
        shop_id: shopItem?.shop_id || "0",
        // ware_id: warehouse?.id || "1",
        page,
        search: text,
      };

      const response = await get_product_details(params);

      if (response.data && response.data.data) {
        setProductDetails(response.data.data);
      }
    } catch (error) {
      console.error("Error fetching product details:", error);
      toast.error("Failed to fetch product details");
    } finally {
      hideLoader();
    }
  };


  // Handle form input changes
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: value
    });

    // Clear error when field is changed
    if (errors[name]) {
      setErrors({
        ...errors,
        [name]: ''
      });
    }
  };

  // Handle product selection
  const handleProductSelect = (e) => {
    const productId = e.target.value;
    setFormData({
      ...formData,
      p_id: productId
    });

    // Find selected product
    const product = products.find(p => p.product_id.toString() === productId);
    setSelectedProduct(product);

    if (product) {
      // Pre-fill form with product data
      setFormData(prev => ({
        ...prev,
        p_id: productId,
        p_code: product.product_code || '',
        pbox_unit: product.unit || '',
        p_weight: isEditMode ? prev.p_weight : '',  // Keep weight if editing
        p_price: product.sales_price || '',
        pbox_qty: product.box_qty || '',
        pbox_code: product.box_barcode || '',
        pbox_price: product.box_sales || '',
        reorder_point: product.reorder_point || '',
      }));
    }
  };

  const handleEdit = (detail) => {
    setIsEditMode(true);
    setSelectedDetail(detail);

    setFormData({
      id: detail.id,
      p_id: detail.p_id || '',
      p_code: detail.p_code || '',
      p_weight: detail.p_weight || '',
      // warehouse_id: detail.warehouse_id || selectedWarehouse?.id || '',
      branch_id: detail.branch_id || '',
      shop_id: detail.shop_id || shopItem?.shop_id || '',
      p_price: detail.p_price || '',
      pbox_qty: detail.pbox_qty || '',
      pbox_unit: detail.pbox_unit || '',
      pbox_code: detail.pbox_code || '',
      pbox_price: detail.pbox_price || '',
      reorder_point: detail.reorder_point || '',
      created_by: detail.created_by || UserItem?.user_id || ''
    });

    const product = products.find(p => p.product_id.toString() === detail.p_id);
    setSelectedProduct(product);
    setShowDetailsModal(true);
  };

  const handleDeleteClick = (detail) => {
    setSelectedDetail(detail);
    setShowDeleteModal(true);
  };

  const handleDelete = async () => {
    if (!selectedDetail) return;

    showLoader();
    try {
      const response = await delete_product_details(selectedDetail.id);

      if (response.data && response.data.status === 'success') {
        toast.success("Product detail deleted successfully");
        setShowDeleteModal(false);
        fetchProductDetails(); // Refresh list
      } else {
        toast.error(response.data?.message || "Failed to delete product detail");
      }
    } catch (error) {
      console.error("Error deleting product detail:", error);
      toast.error(error?.response?.data?.message || "Error deleting product detail");
    } finally {
      hideLoader();
    }
  };

  const openAdjustModal = (detail) => {
    setAdjustDetail(detail);
    setAdjustQty("");
    setAdjustPrice(detail.p_price || "");
    setAdjustType("with_val");
    setAdjustAction("add");
    setAdjustDate(moment().format("YYYY-MM-DD"));
    setShowAdjustModal(true);
  };

  const handleAdjustSubmit = async (e) => {
    e.preventDefault();
    const stockChange = parseFloat(adjustQty);
    if (!stockChange || isNaN(stockChange) || stockChange <= 0) {
      toast.error("Please enter a valid quantity greater than 0");
      return;
    }

    const packWeight = parseFloat(adjustDetail.p_weight || 0);
    const isLosePack = adjustDetail.pbox_code === "FREE SIZE / LOSE QTY" || packWeight <= 0;

    // Calculate packets change
    const qtyChange = isLosePack ? stockChange : stockChange / packWeight;

    showLoader();
    try {
      const payload = {
        pd_id: adjustDetail.id,
        qty: qtyChange,
        action: adjustAction,
        price: adjustPrice,
        is_value_adjustment: adjustType === "with_val",
        shop_id: shopItem?.id || shopItem?.shop_id || "",
        user_id: UserItem?.user_id || "1",
        date: adjustDate,
      };

      const response = await adjustProductDetailsStock(payload);
      if (response.data && response.data.status === "success") {
        toast.success("Stock adjusted successfully");
        setShowAdjustModal(false);
        setAdjustQty("");
        fetchProductDetails();
      } else {
        toast.error(response.data?.message || "Failed to adjust stock");
      }
    } catch (err) {
      console.error("Adjustment error", err);
      toast.error(err.response?.data?.message || "Failed to adjust stock");
    } finally {
      hideLoader();
    }
  };

  // Download Sample Excel Template for Bulk Upload
  const downloadSampleTemplate = () => {
    const sampleData = [
      {
        "Product Name": "BLACK PEPPER KMT",
        "Product Code": "BP-10G",
        "Weight": 10,
        "Unit": "grams",
        "Packets (Stock Qty)": 100,
        "Selling Price (INR)": 3.4,
        "Costing (INR)": 49.39,
        "Reorder Point": 20,
        "Box Quantity": 50,
        "Box Price (INR)": 160
      },
      {
        "Product Name": "BLACK PEPPER KMT",
        "Product Code": "BP-50G",
        "Weight": 50,
        "Unit": "grams",
        "Packets (Stock Qty)": 50,
        "Selling Price (INR)": 14,
        "Costing (INR)": 49.39,
        "Reorder Point": 10,
        "Box Quantity": 20,
        "Box Price (INR)": 260
      },
      {
        "Product Name": "AFSAR 65",
        "Product Code": "AF-500G",
        "Weight": 500,
        "Unit": "grams",
        "Packets (Stock Qty)": 25,
        "Selling Price (INR)": 67.50,
        "Costing (INR)": 45.00,
        "Reorder Point": 5,
        "Box Quantity": 10,
        "Box Price (INR)": 650
      }
    ];

    const ws = XLSX.utils.json_to_sheet(sampleData);
    ws['!cols'] = [
      { wch: 22 },
      { wch: 15 },
      { wch: 10 },
      { wch: 10 },
      { wch: 20 },
      { wch: 18 },
      { wch: 15 },
      { wch: 15 },
      { wch: 15 },
      { wch: 15 },
    ];
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Product_Packs_Template");
    XLSX.writeFile(wb, "product_packs_bulk_upload_template.xlsx");
  };

  // Handle Bulk Excel File Upload & Parsing
  const handleBulkFileUpload = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    setBulkFileName(file.name);
    setBulkFile(file);

    const reader = new FileReader();
    reader.onload = (evt) => {
      try {
        const bstr = evt.target.result;
        const wb = XLSX.read(bstr, { type: "binary" });
        const wsname = wb.SheetNames[0];
        const ws = wb.Sheets[wsname];
        const rawData = XLSX.utils.sheet_to_json(ws, { defval: "" });

        if (!rawData || rawData.length === 0) {
          toast.error("The uploaded file is empty");
          setBulkParsedData([]);
          return;
        }

        const parsed = rawData.map((row) => {
          const productName = String(row["Product Name"] || row["product_name"] || row["Product"] || row["title"] || "").trim();
          const productCode = String(row["Product Code"] || row["product_code"] || row["p_code"] || "").trim();
          const weight = parseFloat(row["Weight"] || row["weight"] || row["size"] || row["p_weight"] || 0);
          const unit = String(row["Unit"] || row["unit"] || row["pbox_unit"] || "grams").trim().toLowerCase();
          const packets = parseFloat(row["Packets (Stock Qty)"] || row["Packets"] || row["packets"] || row["Stock"] || row["stock"] || row["qty"] || 0);
          const sellingPrice = parseFloat(row["Selling Price (INR)"] || row["Selling Price"] || row["Price (INR)"] || row["price"] || row["p_price"] || 0);
          const costing = parseFloat(row["Costing (INR)"] || row["Costing"] || row["costing"] || row["mfg_price"] || 0);
          const reorderPoint = row["Reorder Point"] || row["reorder_point"] || "";
          const boxQty = row["Box Quantity"] || row["box_qty"] || row["pbox_qty"] || "";
          const boxPrice = row["Box Price (INR)"] || row["Box Price"] || row["box_price"] || row["pbox_price"] || "";

          return {
            product_name: productName,
            product_code: productCode,
            p_weight: isNaN(weight) ? 0 : weight,
            pbox_unit: unit || "grams",
            qty: isNaN(packets) ? 0 : packets,
            p_price: isNaN(sellingPrice) ? 0 : sellingPrice,
            costing: isNaN(costing) ? 0 : costing,
            reorder_point: reorderPoint !== undefined ? String(reorderPoint) : "",
            pbox_qty: boxQty !== undefined ? String(boxQty) : "",
            pbox_price: boxPrice !== undefined ? String(boxPrice) : ""
          };
        }).filter(item => item.product_name);

        if (parsed.length === 0) {
          toast.error("No valid product rows found. Please ensure 'Product Name' column is filled.");
        } else {
          toast.success(`Successfully parsed ${parsed.length} items from Excel.`);
        }

        setBulkParsedData(parsed);
      } catch (error) {
        console.error("Error reading excel file", error);
        toast.error("Failed to parse file. Please upload a valid Excel or CSV file.");
        setBulkParsedData([]);
      }
    };

    reader.readAsBinaryString(file);
  };

  // Submit Bulk Upload
  const handleBulkSubmit = async () => {
    if (bulkParsedData.length === 0) {
      toast.error("No products to upload");
      return;
    }

    setIsUploadingBulk(true);
    showLoader();
    try {
      const payload = {
        shop_id: shopItem?.id || shopItem?.shop_id || "0",
        user_id: UserItem?.user_id || "1",
        date: bulkDate,
        items: bulkParsedData
      };

      const response = await bulkUploadProductDetails(payload);
      if (response.data && response.data.status === "success") {
        toast.success(response.data.message || "Bulk upload completed successfully!");
        setShowBulkModal(false);
        setBulkFile(null);
        setBulkFileName("");
        setBulkParsedData([]);
        fetchProductDetails();
        fetchProducts();
      } else {
        toast.error(response.data?.message || "Failed to upload bulk products");
      }
    } catch (err) {
      console.error("Bulk upload error", err);
      toast.error(err.response?.data?.message || "Error processing bulk upload");
    } finally {
      setIsUploadingBulk(false);
      hideLoader();
    }
  };



  // Validate form
  const validateForm = () => {
    const newErrors = {};

    if (!formData.p_id) newErrors.p_id = "Product selection is required";
    if (!formData.p_weight) newErrors.p_weight = "Weight is required";
    if (!formData.p_price) newErrors.p_price = "Price is required";
    if (!formData.pbox_unit) newErrors.pbox_unit = "Unit is required";
    // pbox_qty, pbox_code, pbox_price, reorder_point are optional

    // Check for duplicate weight and unit in the same product
    const targetPId = formData.p_id?.toString();
    const targetWeight = parseFloat(formData.p_weight);
    const targetUnit = formData.pbox_unit?.trim().toLowerCase();

    if (targetPId && !isNaN(targetWeight) && targetUnit) {
      const isDuplicate = productDetails.some(detail => {
        // Skip comparing with itself when in edit mode
        if (isEditMode && detail.id === formData.id) return false;

        // Skip check for "FREE SIZE / LOSE QTY" default details
        if (detail.pbox_code === "FREE SIZE / LOSE QTY") return false;

        const detPId = detail.p_id?.toString();
        const detWeight = parseFloat(detail.p_weight);
        const detUnit = detail.pbox_unit?.trim().toLowerCase();

        return (
          detPId === targetPId &&
          !isNaN(detWeight) &&
          Math.abs(detWeight - targetWeight) < 0.0001 &&
          detUnit === targetUnit
        );
      });

      if (isDuplicate) {
        newErrors.p_weight = "This product weight already exists.";
        newErrors.pbox_unit = "This product weight already exists.";
        toast.error(`Product weight pack '${formData.p_weight} ${formData.pbox_unit}' already exists for this product.`);
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Handle form submission
  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) return;

    setLoading(true);
    showLoader();

    try {
      let response;

      if (isEditMode) {
        response = await update_product_details(formData.id, formData);
        // response = await (formData);
      } else {
        response = await create_product_details(formData);
      }

      if (response.data && response.data.status === 'success') {
        toast.success(isEditMode ? "Product details updated successfully" : "Product details added successfully");
        resetForm();
        setShowDetailsModal(false);
        fetchProductDetails();
      } else {
        toast.error(response.data?.message || (isEditMode ? "Failed to update product details" : "Failed to add product details"));
      }
    } catch (error) {
      console.error(isEditMode ? "Error updating product details:" : "Error adding product details:", error);
      toast.error(error?.response?.data?.message || (isEditMode ? "Error updating product details" : "Error adding product details"));
    } finally {
      setLoading(false);
      hideLoader();
    }
  };

  // Reset form
  const resetForm = () => {
    setFormData({
      p_id: '',
      p_code: '',
      p_weight: '',
      // warehouse_id: selectedWarehouse?.id || '',
      branch_id: '',
      shop_id: shopItem?.shop_id || '',
      p_price: '',
      pbox_qty: '',
      pbox_unit: '',
      pbox_code: '',
      pbox_price: '',
      reorder_point: '',
      created_by: UserItem?.user_id || ''
    });
    setSelectedProduct(null);
    setSelectedDetail(null);
    setIsEditMode(false);
    setErrors({});
  };

  // Format date for display
  const formatDate = (dateString) => {
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString();
    } catch (error) {
      return dateString;
    }
  };

  const formatAvailableStock = (detail) => {
    const qty = parseFloat(detail.p_qty || 0);
    const weight = parseFloat(detail.p_weight || 0);
    const unit = (detail.pbox_unit || "").toLowerCase();

    if (qty <= 0 || weight <= 0) {
      if (unit.includes("kg") || unit.includes("kilo")) {
        return "0 kg";
      }
      return "0 grams";
    }

    if (unit.includes("kg") || unit.includes("kilo")) {
      const totalKg = qty * weight;
      return `${Number.isInteger(totalKg) ? totalKg : totalKg.toFixed(2)} kg`;
    } else if (unit.includes("gram") || unit === "g") {
      const totalGrams = qty * weight;
      if (totalGrams >= 1000) {
        const totalKg = totalGrams / 1000;
        return `${Number.isInteger(totalKg) ? totalKg : totalKg.toFixed(2)} kg`;
      } else {
        return `${Number.isInteger(totalGrams) ? totalGrams : totalGrams.toFixed(2)} grams`;
      }
    } else {
      const total = qty * weight;
      return `${Number.isInteger(total) ? total : total.toFixed(2)} ${detail.pbox_unit || ""}`;
    }
  };

  const filteredDetails = productDetails.filter(detail => {
    // Dropdown filter by Product
    if (filterProductId && detail.p_id && detail.p_id.toString() !== filterProductId.toString()) {
      return false;
    }

    // Debounced Search filter
    if (debouncedSearch.trim()) {
      const query = debouncedSearch.toLowerCase().trim();
      const productObj = products.find(p => p.product_id.toString() === detail.p_id?.toString());
      const productName = (productObj?.title || `product #${detail.p_id}`).toLowerCase();
      const productCode = (detail.p_code || "").toLowerCase();
      const weightText = (detail.pbox_code === "FREE SIZE / LOSE QTY" ? "lose packs" : `${detail.p_weight} ${detail.pbox_unit}`).toLowerCase();
      const priceText = (detail.p_price || "").toString().toLowerCase();
      const costingText = (detail.costing || detail.mfg_price || "").toString().toLowerCase();
      const reorderText = (detail.reorder_point || "").toString().toLowerCase();

      const matches =
        productName.includes(query) ||
        productCode.includes(query) ||
        weightText.includes(query) ||
        priceText.includes(query) ||
        costingText.includes(query) ||
        reorderText.includes(query);

      if (!matches) return false;
    }

    return true;
  });

  return (
    <div className="page-wrappers">
      <div className="page-content">
        <NaviGate />

        <div className="mb-0 d-flex justify-content-between align-items-center flex-wrap gap-2">
          <h6 className="mb-0 text-uppercase" style={{ fontFamily: "poppins" }}>
            Product Packs
          </h6>
          <div className="d-flex align-items-center gap-2 flex-wrap">
            {/* Search Input with Debounce */}
            <div className="position-relative" style={{ minWidth: "250px" }}>
              <input
                type="text"
                className="form-control ps-5"
                placeholder="Search product"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
              />
              <Search
                size={18}
                className="position-absolute top-50 start-0 translate-middle-y ms-3 text-secondary"
                style={{ pointerEvents: "none" }}
              />
              {searchQuery && (
                <button
                  type="button"
                  className="btn btn-link position-absolute top-50 end-0 translate-middle-y me-1 p-1 text-secondary text-decoration-none"
                  onClick={() => setSearchQuery("")}
                >
                  <X size={16} />
                </button>
              )}
            </div>

            {/* Filter by Product Dropdown */}
            <div style={{ minWidth: "220px" }}>
              <select
                className="form-select"
                value={filterProductId}
                onChange={(e) => setFilterProductId(e.target.value)}
              >
                <option value="">Filter by Product (All)</option>
                {products.map(product => (
                  <option key={product.product_id} value={product.product_id}>
                    {product.title}
                  </option>
                ))}
              </select>
            </div>

            <button
              type="button"
              className="btn btn-outline-primary text-nowrap d-flex align-items-center gap-1"
              onClick={() => {
                setBulkFile(null);
                setBulkFileName("");
                setBulkParsedData([]);
                setBulkDate(moment().format("YYYY-MM-DD"));
                setShowBulkModal(true);
              }}
            >
              <Upload size={16} /> Bulk Upload
            </button>

            <button
              type="button"
              className="btn btn-primary text-nowrap"
              onClick={() => {
                resetForm(); // Clear form before opening
                setShowDetailsModal(true);
              }}
            >
              Add Product Packs
            </button>
          </div>
        </div>
        <hr className="mt-1" />

        {/* Product Details Table */}
        <div className="card">
          <div className="card-body">
            <div className="table-responsive">
              <table className="table table-striped table-bordered text-nowrap">
                <thead>
                  <tr>
                    <th>No</th>
                    <th>Product Code</th>
                    <th>Product</th>
                    <th>Weight</th>
                    <th>Packets</th>
                    <th>Avl Stock</th>
                    <th>Price (INR)</th>
                    <th>Costing (INR)</th>
                    {/* <th>Box Qty</th> */}
                    {/* <th>Box Unit</th> */}
                    {/* <th>Box Code</th> */}
                    {/* <th>Box Price (INR)</th> */}
                    <th>Reorder Point</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredDetails.length > 0 ? (
                    filteredDetails.map((detail, index) => (
                      <tr key={detail.id}>
                        <td>{index + 1}</td>
                        <td>{detail.p_code}</td>
                        <td style={{ maxWidth: "200px", whiteSpace: "normal", overflowWrap: "break-word" }}>
                          {products.find(p => p.product_id.toString() === detail.p_id)?.title ||
                            `Product #${detail.p_id}`}
                        </td>
                        <td>
                          {detail.pbox_code === "FREE SIZE / LOSE QTY" ? "Lose Packs" : `${detail.p_weight} ${detail.pbox_unit}`}
                        </td>
                        <td>{detail.p_qty || 0}</td>
                        <td>{formatAvailableStock(detail)}</td>
                        <td>₹{detail.p_price}</td>
                        <td>₹{parseFloat(detail.costing || detail.mfg_price || 0).toFixed(2)}</td>
                        {/* <td>{detail.pbox_qty}</td> */}
                        {/* <td>{detail.pbox_unit}</td> */}
                        {/* <td>{detail.pbox_code}</td> */}
                        {/* <td>{detail.pbox_price}</td> */}
                        <td>{detail.reorder_point}</td>
                        <td>
                          {detail.pbox_code === "FREE SIZE / LOSE QTY" ? (
                            <div className="d-flex align-items-center gap-2 justify-content-center">
                              <span className="badge bg-secondary">System Default</span>
                              <img
                                src="/assets/images/icons/transfer-switch.png"
                                onClick={() => openAdjustModal(detail)}
                                height="16px"
                                alt="Adjust Stock"
                                style={{ cursor: "pointer" }}
                                title="Adjust Stock"
                              />
                            </div>
                          ) : (
                            <>
                              <img
                                src="/assets/images/icons/edit.png"
                                onClick={() => handleEdit(detail)}
                                height="16px"
                                alt="Edit"
                                style={{ cursor: "pointer" }}
                                title="Edit"
                              />
                              &nbsp;&nbsp;&nbsp;
                              <img
                                src="/assets/images/icons/transfer-switch.png"
                                onClick={() => openAdjustModal(detail)}
                                height="16px"
                                alt="Adjust Stock"
                                style={{ cursor: "pointer" }}
                                title="Adjust Stock"
                              />
                              &nbsp;&nbsp;&nbsp;
                              <img
                                src="/assets/images/icons/delete.png"
                                onClick={() => handleDeleteClick(detail)}
                                height="16px"
                                alt="Delete"
                                style={{ cursor: "pointer" }}
                                title="Delete"
                              />
                            </>
                          )}
                        </td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan="10" className="text-center">
                        No product details found
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>

        {/* Add/Edit Details Modal */}
        {showDetailsModal && (
          <div className="modal fade show" style={{ display: "block" }} tabIndex="-1">
            <div className="modal-dialog modal-lg">
              <div className="modal-content">
                <div className="modal-header">
                  <h5 className="modal-title">{isEditMode ? 'Edit Product Packs' : 'Add Product Packs'}</h5>
                  <button
                    type="button"
                    className="btn-close"
                    onClick={() => {
                      setShowDetailsModal(false);
                      resetForm();
                    }}
                  ></button>
                </div>
                <div className="modal-body">
                  <form onSubmit={handleSubmit}>
                    <div className="row g-3">
                      {/* Product Selection */}
                      <div className="col-md-6"  >
                        <label className="form-label">Select Product <span className="text-danger">*</span></label>
                        <select
                          name="p_id"
                          value={formData.p_id}
                          onChange={handleProductSelect}
                          className={`form-control ${errors.p_id ? 'is-invalid' : ''}`}
                          disabled={isEditMode}
                        >
                          <option value="">Select Product</option>
                          {products.map(product => (
                            <option key={product.product_id} value={product.product_id}>
                              {product.title}
                            </option>
                          ))}
                        </select>
                        {errors.p_id && <div className="invalid-feedback">{errors.p_id}</div>}
                      </div>
                      {/* Product Code */}
                      <div className="col-md-6">
                        <label className="form-label">Product Code</label>
                        <input
                          type="text"
                          name="p_code"
                          value={formData.p_code}
                          onChange={handleInputChange}
                          className="form-control"

                        />
                      </div>

                      <div className="row mx-1 mt-1 mb-2">


                        {/* Product Weight */}
                        <div className="col-md-6 mb-1">
                          <label className="form-label">P_Weight <span className="text-danger">*</span></label>
                          <input
                            type="text"
                            name="p_weight"
                            value={formData.p_weight}
                            onChange={handleInputChange}
                            onInput={(e) => {
                              e.target.value = e.target.value.replace(/[^0-9.]/g, '');
                              const parts = e.target.value.split('.');
                              if (parts.length > 2) {
                                e.target.value = parts[0] + '.' + parts.slice(1).join('');
                              }
                              if (parts[1] && parts[1].length > 3) {
                                e.target.value = parts[0] + '.' + parts[1].substring(0, 3);
                              }
                            }}
                            inputMode='decimal'
                            pattern="[0-9]*\.?[0-9]*"
                            placeholder="0.00"
                            className={`form-control ${errors.p_weight ? 'is-invalid' : ''}`}
                          />
                          {errors.p_weight && <div className="invalid-feedback">{errors.p_weight}</div>}
                        </div>

                        <div className="col-md-6">
                          <label className="form-label">Unit <span className="text-danger">*</span></label>
                          <select
                            name="pbox_unit"
                            value={formData.pbox_unit}
                            onChange={handleInputChange}
                            className={`form-control ${errors.pbox_unit ? 'is-invalid' : ''}`}
                          >
                            <option value="">Select Unit</option>
                            <option value="kilograms">Kilograms</option>
                            <option value="grams">Grams</option>
                            {/* <option value="liters">Liters</option>
                            <option value="milliliters">Milliliters</option>
                            <option value="pcs">Pieces</option> */}
                          </select>
                          {errors.pbox_unit && <div className="invalid-feedback">{errors.pbox_unit}</div>}
                        </div>
                        {/* Product Price */}
                        <div className="col-md-6 mb-1">
                          <label className="form-label">Price (INR) <span className="text-danger">*</span></label>
                          <input
                            name="p_price"
                            value={formData.p_price}
                            onChange={handleInputChange}
                            onInput={(e) => {
                              e.target.value = e.target.value.replace(/[^0-9.]/g, '');
                              const parts = e.target.value.split('.');
                              if (parts.length > 2) {
                                e.target.value = parts[0] + '.' + parts.slice(1).join('');
                              }
                              if (parts[1] && parts[1].length > 3) {
                                e.target.value = parts[0] + '.' + parts[1].substring(0, 3);
                              }
                            }}
                            inputMode='decimal'
                            pattern="[0-9]*\.?[0-9]*"
                            placeholder="0.00"
                            className={`form-control ${errors.p_price ? 'is-invalid' : ''}`}
                            min="0"
                            step="0.01"
                          />
                          {errors.p_price && <div className="invalid-feedback">{errors.p_price}</div>}
                        </div>
                      </div>

                      {/* Box Quantity */}
                      <div className="row mx-1 pt-3">

                        <div className="col-md-6">
                          <label className="form-label">Box Quantity</label>
                          <input
                            type="number"
                            name="pbox_qty"
                            value={formData.pbox_qty}
                            onChange={handleInputChange}
                            className="form-control"
                            min="1"
                          />
                        </div>
                        {/* Box Unit */}



                        {/* Box Code */}
                        <div className="col-md-6 mb-2">
                          <label className="form-label">Box Code</label>
                          <input
                            type="text"
                            name="pbox_code"
                            value={formData.pbox_code}
                            onChange={handleInputChange}
                            className="form-control"
                          />
                        </div>

                        {/* Box Price */}
                        <div className="col-md-6 mb-2">
                          <label className="form-label">Box Price (INR)</label>
                          <input
                            type="number"
                            name="pbox_price"
                            value={formData.pbox_price}
                            onChange={handleInputChange}
                            className="form-control"
                            min="0"
                            step="0.01"
                          />
                        </div>

                        <div className="col-md-6">
                          <label className="form-label">Reorder Point</label>
                          <input
                            type="text"
                            name="reorder_point"
                            value={formData.reorder_point}
                            onChange={handleInputChange}
                            className="form-control"
                          />
                        </div>
                      </div>
                    </div>

                    <div className="text-end mt-4">
                      <button
                        type="button"
                        className="btn btn-secondary me-2"
                        onClick={() => {
                          setShowDetailsModal(false);
                          resetForm();
                        }}
                      >
                        Cancel
                      </button>
                      <button type="submit" className="btn btn-primary" disabled={loading}>
                        {loading ? 'Saving...' : isEditMode ? 'Update Details' : 'Save Details'}
                      </button>
                    </div>
                  </form>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Delete Confirmation Modal */}
        {showDeleteModal && selectedDetail && (
          <div className="modal fade show" style={{ display: "block" }} tabIndex="-1">
            <div className="modal-dialog modal-md">
              <div className="modal-content">

                <div className="modal-header">
                  <h5 className="modal-title">Confirm Delete</h5>
                  <button
                    type="button"
                    className="btn-close"
                    onClick={() => setShowDeleteModal(false)}
                  ></button>
                </div>

                <div className="modal-body text-center">
                  <i className="bi bi-exclamation-triangle text-danger fs-1 d-block mb-3"></i>

                  {/* PRODUCT DETAIL LINE */}
                  <p className="fw-bold">
                    {products.find(p => p.product_id.toString() === selectedDetail.p_id)?.title}
                    {" — "}
                    {selectedDetail.p_weight} {selectedDetail.pbox_unit} Remaining QTY {selectedDetail.p_qty || 0}
                  </p>

                  <p className="mt-2">
                    Are you sure you want to delete this item?
                    <br />
                    <small className="text-danger">This action cannot be undone.</small>
                  </p>
                </div>

                <div className="modal-footer">
                  <button
                    type="button"
                    className="btn btn-secondary"
                    onClick={() => setShowDeleteModal(false)}
                  >
                    Cancel
                  </button>

                  <button type="button" className="btn btn-danger" onClick={handleDelete}>
                    Delete
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Stock Adjustment Modal */}
        {showAdjustModal && adjustDetail && (
          <div className="modal fade show" style={{ display: "block", backgroundColor: "rgba(0,0,0,0.5)", zIndex: 1050 }} tabIndex="-1">
            <div className="modal-dialog modal-md modal-dialog-centered">
              <div className="modal-content">
                <div className="modal-header">
                  <h5 className="modal-title">Stock Adjustment</h5>
                  <button
                    type="button"
                    className="btn-close"
                    onClick={() => {
                      setShowAdjustModal(false);
                      setAdjustQty("");
                    }}
                  ></button>
                </div>
                <div className="modal-body">
                  <form onSubmit={handleAdjustSubmit}>
                    <div className="p-3 bg-light rounded mb-3">
                      <p className="mb-1">
                        Product: <strong>{products.find(p => p.product_id.toString() === adjustDetail.p_id.toString())?.title || `Product #${adjustDetail.p_id}`}</strong>
                      </p>
                      <p className="mb-1">
                        Pack Weight: <strong>{adjustDetail.pbox_code === "FREE SIZE / LOSE QTY" ? "Lose Packs" : `${adjustDetail.p_weight} ${adjustDetail.pbox_unit}`}</strong>
                      </p>
                      <p className="mb-1">
                        Current Packets: <strong>{adjustDetail.p_qty || 0}</strong>
                      </p>
                      <p className="mb-0">
                        Current Available Stock: <strong>{formatAvailableStock(adjustDetail)}</strong>
                      </p>
                    </div>

                    <div className="mb-3">
                      <label className="form-label">Adjustment Date <span className="text-danger">*</span></label>
                      <input
                        type="date"
                        className="form-control"
                        value={adjustDate}
                        onChange={(e) => setAdjustDate(e.target.value)}
                        required
                      />
                    </div>

                    <div className="mb-3">
                      <label className="form-label">Adjustment Action <span className="text-danger">*</span></label>
                      <div className="d-flex gap-4">
                        <div className="form-check">
                          <input
                            className="form-check-input"
                            type="radio"
                            name="adjustAction"
                            id="actionAdd"
                            value="add"
                            checked={adjustAction === "add"}
                            onChange={(e) => setAdjustAction(e.target.value)}
                          />
                          <label className="form-check-label" htmlFor="actionAdd">
                            Add Stock (UP)
                          </label>
                        </div>
                        <div className="form-check">
                          <input
                            className="form-check-input"
                            type="radio"
                            name="adjustAction"
                            id="actionDeduct"
                            value="deduct"
                            checked={adjustAction === "deduct"}
                            onChange={(e) => setAdjustAction(e.target.value)}
                          />
                          <label className="form-check-label" htmlFor="actionDeduct">
                            Deduct Stock (DOWN)
                          </label>
                        </div>
                      </div>
                    </div>

                    <div className="mb-3">
                      <label className="form-label">
                        Stock to Adjust ({adjustDetail.pbox_code === "FREE SIZE / LOSE QTY" ? (adjustDetail.pbox_unit || "kg") : (adjustDetail.pbox_unit || "grams")}) <span className="text-danger">*</span>
                      </label>
                      <input
                        type="number"
                        step="any"
                        min="0.001"
                        className="form-control"
                        value={adjustQty}
                        onChange={(e) => setAdjustQty(e.target.value)}
                        placeholder={`Enter stock in ${adjustDetail.pbox_code === "FREE SIZE / LOSE QTY" ? (adjustDetail.pbox_unit || "kg") : (adjustDetail.pbox_unit || "grams")}`}
                        required
                      />
                      {adjustQty && !isNaN(adjustQty) && (
                        <div className="form-text text-primary mt-1 font-weight-1">
                          {(() => {
                            const packWeight = parseFloat(adjustDetail.p_weight || 0);
                            const isLosePack = adjustDetail.pbox_code === "FREE SIZE / LOSE QTY" || packWeight <= 0;
                            if (isLosePack) {
                              return `Equivalent change: ${parseFloat(adjustQty).toFixed(2)} ${adjustDetail.pbox_unit || "kg"}`;
                            } else {
                              const packets = parseFloat(adjustQty) / packWeight;
                              return `Equivalent change: ${packets.toFixed(2)} packets (calculated based on pack weight of ${adjustDetail.p_weight} ${adjustDetail.pbox_unit || "grams"})`;
                            }
                          })()}
                        </div>
                      )}
                    </div>

                    <div className="mb-3">
                      <label className="form-label">Price (INR) <span className="text-danger">*</span></label>
                      <input
                        type="number"
                        step="any"
                        min="0"
                        className="form-control"
                        value={adjustPrice}
                        onChange={(e) => setAdjustPrice(e.target.value)}
                        placeholder="Enter price for adjustment"
                        required
                      />
                    </div>

                    <div className="mb-3">
                      <label className="form-label">Adjustment Type <span className="text-danger">*</span></label>
                      <select
                        className="form-select"
                        value={adjustType}
                        onChange={(e) => setAdjustType(e.target.value)}
                      >
                        <option value="with_val">With Value (Reflects in Trial Balance)</option>
                        <option value="without_val">Without Value (No Ledger Entry)</option>
                      </select>
                    </div>

                    <div className="text-end mt-4">
                      <button
                        type="button"
                        className="btn btn-secondary me-2"
                        onClick={() => {
                          setShowAdjustModal(false);
                          setAdjustQty("");
                        }}
                      >
                        Cancel
                      </button>
                      <button type="submit" className="btn btn-primary">
                        Save Adjustment
                      </button>
                    </div>
                  </form>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Bulk Upload Modal */}
        {showBulkModal && (
          <div className="modal fade show" style={{ display: "block", backgroundColor: "rgba(0,0,0,0.5)", zIndex: 1050 }} tabIndex="-1">
            <div className="modal-dialog modal-xl modal-dialog-centered modal-dialog-scrollable">
              <div className="modal-content">
                <div className="modal-header bg-light">
                  <div className="d-flex align-items-center gap-2">
                    <FileSpreadsheet className="text-success" size={24} />
                    <h5 className="modal-title mb-0">Bulk Upload Products & Packs</h5>
                  </div>
                  <button
                    type="button"
                    className="btn-close"
                    onClick={() => {
                      setShowBulkModal(false);
                      setBulkFile(null);
                      setBulkFileName("");
                      setBulkParsedData([]);
                    }}
                  ></button>
                </div>

                <div className="modal-body">
                  {/* Instructions & Template Download Banner */}
                  <div className="alert alert-info d-flex justify-content-between align-items-center flex-wrap gap-2 mb-3">
                    <div>
                      <strong>How it works:</strong>
                      <ul className="mb-0 ps-3 mt-1 small">
                        <li>Upload an Excel (<strong>.xlsx</strong>, <strong>.xls</strong>) or <strong>.csv</strong> file.</li>
                        <li><strong>New Products/Packs:</strong> If the product or pack doesn't exist, it will be automatically created.</li>
                        <li><strong>Existing Products/Packs:</strong> Product details (price, code, etc.) will be updated, and any quantity/costing specified will be <strong>accumulated/added to stock</strong> (similar to stock adjustments).</li>
                      </ul>
                    </div>
                    <button
                      type="button"
                      className="btn btn-sm btn-outline-primary d-flex align-items-center gap-1 shadow-sm text-nowrap"
                      onClick={downloadSampleTemplate}
                    >
                      <Download size={15} /> Download Sample Template
                    </button>
                  </div>

                  {/* Upload Controls Row */}
                  <div className="row g-3 mb-4 p-3 bg-light rounded border">
                    <div className="col-md-5">
                      <label className="form-label fw-bold">Stock Entry Date <span className="text-danger">*</span></label>
                      <input
                        type="date"
                        className="form-control"
                        value={bulkDate}
                        onChange={(e) => setBulkDate(e.target.value)}
                        required
                      />
                      <small className="text-muted">Date recorded on stock addition transactions & ledger.</small>
                    </div>

                    <div className="col-md-7">
                      <label className="form-label fw-bold">Select Excel / CSV File <span className="text-danger">*</span></label>
                      <input
                        type="file"
                        className="form-control"
                        accept=".xlsx, .xls, .csv"
                        onChange={handleBulkFileUpload}
                      />
                      {bulkFileName && (
                        <small className="text-success fw-bold d-block mt-1">
                          Selected: {bulkFileName} ({bulkParsedData.length} valid rows detected)
                        </small>
                      )}
                    </div>
                  </div>

                  {/* Preview Table */}
                  {bulkParsedData.length > 0 ? (
                    <div>
                      <div className="d-flex justify-content-between align-items-center mb-2">
                        <h6 className="mb-0 fw-bold">Data Preview ({bulkParsedData.length} items ready to upload)</h6>
                        <span className="badge bg-primary">{bulkParsedData.length} Rows</span>
                      </div>
                      <div className="table-responsive border rounded" style={{ maxHeight: "350px" }}>
                        <table className="table table-sm table-hover table-bordered mb-0 text-nowrap">
                          <thead className="table-light sticky-top">
                            <tr>
                              <th>#</th>
                              <th>Product Name</th>
                              <th>Product Code</th>
                              <th>Weight</th>
                              <th>Unit</th>
                              <th>Packets (Stock Qty)</th>
                              <th>Selling Price (INR)</th>
                              <th>Costing (INR)</th>
                              <th>Reorder Point</th>
                              <th>Box Qty</th>
                              <th>Box Price (INR)</th>
                            </tr>
                          </thead>
                          <tbody>
                            {bulkParsedData.map((row, idx) => (
                              <tr key={idx}>
                                <td>{idx + 1}</td>
                                <td className="fw-semibold">{row.product_name}</td>
                                <td>{row.product_code || "-"}</td>
                                <td>{row.p_weight}</td>
                                <td>{row.pbox_unit}</td>
                                <td className="fw-bold text-success">+{row.qty}</td>
                                <td>₹{row.p_price}</td>
                                <td>₹{row.costing}</td>
                                <td>{row.reorder_point || "-"}</td>
                                <td>{row.pbox_qty || "-"}</td>
                                <td>{row.pbox_price ? `₹${row.pbox_price}` : "-"}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  ) : (
                    <div className="text-center py-4 text-muted border rounded bg-white">
                      <FileSpreadsheet size={42} className="text-secondary mb-2 opacity-50" />
                      <p className="mb-0">Please choose an Excel (.xlsx) file or download the sample template to preview items.</p>
                    </div>
                  )}
                </div>

                <div className="modal-footer bg-light">
                  <button
                    type="button"
                    className="btn btn-secondary"
                    onClick={() => {
                      setShowBulkModal(false);
                      setBulkFile(null);
                      setBulkFileName("");
                      setBulkParsedData([]);
                    }}
                    disabled={isUploadingBulk}
                  >
                    Cancel
                  </button>
                  <button
                    type="button"
                    className="btn btn-primary d-flex align-items-center gap-1"
                    onClick={handleBulkSubmit}
                    disabled={bulkParsedData.length === 0 || isUploadingBulk}
                  >
                    <Upload size={16} />
                    {isUploadingBulk ? 'Uploading...' : `Upload & Process ${bulkParsedData.length} Items`}
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

      </div>
    </div>
  )
}

export default Pod_details
